import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { HiDocumentReport } from "react-icons/hi";
import EditAtdLayout from '../../components/EditAtdLayout';
import { FaEdit } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './reports.css';

// Dummy data for demonstration
const dummyData = Array.from({ length: 30 }, (_, index) => ({
  date: `2024-08-${index % 31 + 1}`,
  checkIn: `08:${index % 60}`,
  checkOut: `17:${index % 60}`,
  workingHours: index % 12,
  overtime: index % 5,
  overtimeStatus: index % 2 === 0 ? 'Approved' : 'Pending',
  status: index % 2 === 0 ? 'Present' : 'Absent'
}));

export default function Reports() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [data, setData] = useState(dummyData); // Using dummy data
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    if (showTable) {
      // Simulate data fetching or replace this with actual data fetching
      fetchData().then(fetchedData => setData(fetchedData));
    }
  }, [showTable]);

  const fetchData = async () => {
    // Replace this with actual API call
    return dummyData;
  };

  const handleGenerateReport = () => {
    const errors = {};
    if (!startDate) {
      errors.startDate = "Start date is required";
    }
    if (!endDate) {
      errors.endDate = "End date is required";
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
    } else {
      setFormErrors({});
      setShowTable(true);
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, 'report.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Attendance Report', 14, 16);
    doc.autoTable({
      head: [['Date', 'CheckIn', 'CheckOut', 'Worked Hours', 'Overtime', 'Overtime Status', 'Status']],
      body: data.map(item => [
        item.date,
        item.checkIn,
        item.checkOut,
        item.workingHours,
        item.overtime,
        item.overtimeStatus,
        item.status,
      ]),
    });
    doc.save('report.pdf');
  };

  // Pagination logic
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const currentData = data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Calculate page range
  const pageRange = () => {
    const pages = [];
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Define rowsPerPage and totalEntries
  const rowsPerPage = itemsPerPage; // or just use itemsPerPage directly
  const totalEntries = data.length; // Total number of entries

  return (
    <EditAtdLayout>
      <h1 className='reports-title'><HiDocumentReport />Attendance Date</h1>
      <hr />
      <Form className='reports-form'>
        <Row>
          <Col md={6} xs={12}>
            <Form.Group>
              <Form.Label>Start date <span className='required'>*</span> :</Form.Label>
              <Form.Control
                type="date"
                id="reports_date"
                name="reports_date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                isInvalid={!!formErrors.startDate}
              />
              <Form.Control.Feedback type="invalid">{formErrors.startDate}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} xs={12}>
            <Form.Group>
              <Form.Label>End date <span className='required'>*</span> :</Form.Label>
              <Form.Control
                type="date"
                id="reports_end_date"
                name="reports_end_date"
                required
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                isInvalid={!!formErrors.endDate}
              />
              <Form.Control.Feedback type="invalid">{formErrors.endDate}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Button
          className='generate-report-button'
          type='button'
          onClick={handleGenerateReport}
        >
          Generate Report
        </Button>
      </Form>

      {showTable && (
        <div className='reports-table-container'>
          <div className='table-actions'>
            <Row className='w-100'>
              <Col xs={12} md={6} className='d-flex align-items-center justify-content-start'>
                <Button variant="secondary" className='export-button' onClick={exportToExcel}>Excel</Button>
                <Button variant="secondary" className='export-button' onClick={exportToPDF}>PDF</Button>
              </Col>
              <Col xs={12} md={6} className='d-flex align-items-center justify-content-end'>
                <Form className="d-flex justify-content-end">
                  <Form.Control
                    type="search"
                    placeholder="Search"
                    aria-label="Search"
                    className="search-bar"
                  />
                </Form>
              </Col>
            </Row>
          </div>
          <div className="table-responsive">
            <table className='table table-striped'>
              <thead className='reports-table-header'>
                <tr>
                  <th>Date</th>
                  <th>CheckIn</th>
                  <th>CheckOut</th>
                  <th>Worked Hours</th>
                  <th>Overtime</th>
                  <th>Overtime Status</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index} style={{ textAlign: 'center' }}>
                    <td>{item.date}</td>
                    <td>{item.checkIn}</td>
                    <td>{item.checkOut}</td>
                    <td>{item.workingHours}</td>
                    <td>{item.overtime}</td>
                    <td>{item.overtimeStatus}</td>
                    <td>{item.status}</td>
                    <td>
                      <Link to='/add-atd'>
                        <Button variant='warning'size='sm'><FaEdit /></Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className='table-footer'>
                <tr className='reports-table-footer'>
                  <td colSpan="8" style={{ textAlign: 'left' }}>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, data.length)} of {data.length} entries
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="entries-pagination-container">
            <p className="showing-entries">
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, totalEntries)} of{" "}
              {totalEntries} entries
            </p>

            <nav className="pagination-container">
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                </li>
                {pageRange().map((page) => (
                  <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </EditAtdLayout>
  );
}
